import React, { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Send, Loader2, Copy, Check, ListTree } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { modelServicePost } from '../../utils/modelService';

// Call model service directly — no Rails middleman
async function callGenerate(message, chatHistory = []) {
    const response = await modelServicePost('/generate', { message, chat_history: chatHistory });
    const data = await response.json();
    const results = Array.isArray(data) ? data : [data];
    return results[0]?.content || '';
}

async function callSummarize(text) {
    const response = await modelServicePost('/summarize', { text, max_length: 500 });
    return await response.json();
}

function buildConversationText(chatMessages, limit = 50) {
    const recent = chatMessages.slice(-limit);
    return recent
        .filter(m => m.message_type !== 'system')
        .map(m => {
            const name = m.user?.first_name || m.user?.username || 'Unknown';
            const replyPrefix = m.reply_to
                ? ` [replying to ${m.reply_to.user?.first_name || m.reply_to.user?.username || 'Unknown'}: "${m.reply_to.content?.slice(0, 60)}"]`
                : '';
            return `${name}${replyPrefix}: ${m.content}`;
        })
        .join('\n');
}

export default function TeamAiSidebar({ conversation, context, onClose, onInsertReply, chatMessages = [] }) {
    const { theme } = useTheme();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const autoTriggerRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Auto-trigger action from context on mount
    useEffect(() => {
        if (!context?.type || context.type === 'chat') return;

        // Guard against StrictMode double-invocation
        const triggerKey = `${context.type}-${context.messageId || 'none'}`;
        if (autoTriggerRef.current === triggerKey) return;
        autoTriggerRef.current = triggerKey;

        if (context.type === 'summarize') {
            handleSummarize();
        } else if (context.type === 'translate') {
            handleTranslate(context.messageContent);
        } else if (context.type === 'suggest_reply') {
            handleSuggestReply(context.messageContent);
        }
    }, [context?.type, context?.messageId]);

    const addUserMessage = (text) => {
        setMessages(prev => [...prev, { role: 'user', content: text }]);
    };

    const addAiMessage = (text, type = 'text') => {
        setMessages(prev => [...prev, { role: 'ai', content: text, type }]);
    };

    const handleSummarize = async () => {
        addUserMessage('Summarize this conversation');
        setLoading(true);
        try {
            const text = buildConversationText(chatMessages);
            if (!text) {
                addAiMessage('No messages to summarize.');
                return;
            }
            const data = await callSummarize(text);
            let result = data.summary || '';
            if (data.key_decisions?.length > 0) {
                result += '\n\nKey decisions:\n' + data.key_decisions.map(d => `- ${d}`).join('\n');
            }
            if (data.topics?.length > 0) {
                result += '\n\nTopics:\n' + data.topics.map(t => `- ${t}`).join('\n');
            }
            addAiMessage(result || 'Unable to generate summary.');
        } catch {
            addAiMessage('An error occurred while summarizing.');
        } finally {
            setLoading(false);
        }
    };

    const handleTranslate = async (messageContent) => {
        addUserMessage(`Translate: "${messageContent?.slice(0, 80)}..."`);
        setLoading(true);
        try {
            const prompt = `Translate the following message to English. Return only the translation, nothing else.\n\nMessage: ${messageContent}`;
            const result = await callGenerate(prompt);
            addAiMessage(result || 'No translation returned.');
        } catch {
            addAiMessage('An error occurred while translating.');
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestReply = async (messageContent) => {
        addUserMessage(`Suggest a reply to: "${messageContent?.slice(0, 80)}..."`);
        setLoading(true);
        try {
            const contextText = buildConversationText(chatMessages, 20);
            const prompt = `Based on the conversation context below, suggest a professional reply to this message: "${messageContent}". Return only the suggested reply text.\n\nConversation context:\n${contextText}`;
            const result = await callGenerate(prompt);
            addAiMessage(result || 'No suggestion returned.', 'suggest_reply');
        } catch {
            addAiMessage('An error occurred while generating a suggestion.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendChat = async () => {
        const trimmed = input.trim();
        if (!trimmed || loading) return;

        addUserMessage(trimmed);
        setInput('');
        setLoading(true);

        const sidebarHistory = messages.map(m => ({
            sender: m.role === 'user' ? 'User' : 'Assistant',
            content: m.content
        }));

        try {
            const contextText = buildConversationText(chatMessages, 30);
            const fullMessage = `You are a helpful AI assistant. Here is the conversation context from a team chat:\n\n${contextText}\n\nUser question: ${trimmed}`;
            const result = await callGenerate(fullMessage, sidebarHistory);
            addAiMessage(result || 'No response.');
        } catch {
            addAiMessage('An error occurred. Please try again.');
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendChat();
        }
    };

    const handleCopy = (text, idx) => {
        navigator.clipboard.writeText(text);
        setCopied(idx);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className={`w-full max-w-md rounded-lg shadow-xl flex flex-col max-h-[70vh] overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-400" />
                    <h3 className="font-dm-serif font-semibold text-sm">JPRO Assistant</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={handleSummarize}
                        disabled={loading}
                        className={`p-1.5 rounded transition-colors ${
                            theme === 'dark'
                                ? 'text-purple-400 hover:bg-gray-700'
                                : 'text-purple-600 hover:bg-gray-100'
                        } disabled:opacity-50`}
                        title="Summarize conversation"
                    >
                        <ListTree size={15} />
                    </button>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <Sparkles size={32} className={`mb-3 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                        }`} />
                        <p className={`text-sm font-rasa ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                            Ask a question about this conversation or use the quick actions above.
                        </p>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm font-rasa ${
                            msg.role === 'user'
                                ? 'bg-purple-500 text-white rounded-tr-sm'
                                : theme === 'dark'
                                    ? 'bg-gray-700 text-gray-100 rounded-tl-sm'
                                    : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                        }`}>
                            <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            {msg.role === 'ai' && (
                                <div className="flex items-center gap-1 mt-1.5">
                                    {msg.type === 'suggest_reply' && onInsertReply && (
                                        <button
                                            onClick={() => onInsertReply(msg.content)}
                                            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                                                theme === 'dark'
                                                    ? 'bg-purple-900/40 text-purple-300 hover:bg-purple-900/60'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                            }`}
                                        >
                                            Use as reply
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleCopy(msg.content, idx)}
                                        className={`p-1 rounded transition-colors ${
                                            theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                        title="Copy"
                                    >
                                        {copied === idx ? <Check size={12} /> : <Copy size={12} />}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="flex justify-start">
                        <div className={`flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-rasa rounded-tl-sm ${
                            theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                        }`}>
                            <Loader2 size={14} className="animate-spin" />
                            Thinking...
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className={`border-t px-3 py-3 flex-shrink-0 ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                    theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 focus-within:border-purple-500'
                        : 'bg-gray-50 border-gray-200 focus-within:border-purple-500'
                }`}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about this conversation..."
                        className={`flex-1 text-sm font-rasa bg-transparent focus:outline-none ${
                            theme === 'dark' ? 'text-gray-100 placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'
                        }`}
                    />
                    <button
                        onClick={handleSendChat}
                        disabled={!input.trim() || loading}
                        className={`p-1.5 rounded-lg transition-colors ${
                            input.trim() && !loading
                                ? 'bg-purple-500 text-white hover:bg-purple-600'
                                : theme === 'dark'
                                    ? 'text-gray-500 cursor-not-allowed'
                                    : 'text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        <Send size={14} />
                    </button>
                </div>
            </div>
        </div>
        </div>
    );
}
